import { PLAYER_COLORS, PLAYER_ON_COLOR } from "@/lib/api";

// After the game ends the right column (market, turn controls, hand) is replaced by this
// read-only column: the final scores, then the full game log (chronological, by round).
// The log is marked data-capture="skip" so the board image shows board + players + scores.
export default function FinalColumn({ game }) {
  const fb = game.final_breakdown || {};
  const rows = fb.ranking || fb.breakdowns || [];
  const winners = fb.winner_colors || [];
  const nameOf = (c) => game.players.find((p) => p.color === c)?.name || c;
  const seatOf = (c) => game.players.findIndex((p) => p.color === c) + 1;
  const log = game.game_log || [];
  const groups = [];
  for (const e of log) {
    const r = e.round ?? 0;
    if (!groups.length || groups[groups.length - 1].round !== r) groups.push({ round: r, entries: [] });
    groups[groups.length - 1].entries.push(e);
  }

  return (
    <div data-testid="final-column" className="min-h-0 h-full flex flex-col" style={{ gap: 12 }}>
      <section data-testid="final-scores-panel" className="shrink-0" style={{ background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius)", padding: "12px 14px" }}>
        <div className="flex items-center">
          <span className="section-label">Final scores</span>
          <span className="ml-auto" style={{ fontSize: 12, color: "var(--ink-3)" }}>
            {fb.shared_victory ? `Shared victory: ${winners.map(nameOf).join(" & ")}` : winners.length ? `${nameOf(winners[0])} wins` : ""}
          </span>
        </div>
        <ol className="mt-2 flex flex-col" style={{ gap: 6 }}>
          {rows.map((b, i) => (
            <li key={b.color} data-testid={`final-score-row-${b.color}`} className="flex items-center" style={{ gap: 8, padding: "6px 8px", borderRadius: 8, background: winners.includes(b.color) ? "rgba(36,73,90,.08)" : "transparent" }}>
              <span className="shrink-0 tabular-nums" style={{ width: 16, fontSize: 13, fontWeight: 700, color: "var(--ink-3)" }}>{b.place ?? i + 1}.</span>
              <span className="shrink-0 inline-flex items-center justify-center" style={{ width: 18, height: 18, borderRadius: 4, background: PLAYER_COLORS[b.color], color: PLAYER_ON_COLOR[b.color], fontSize: 10.5, fontWeight: 700 }}>
                {seatOf(b.color)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate" style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>{nameOf(b.color)}</span>
                <span className="block truncate" style={{ fontSize: 11.5, color: "var(--ink-3)" }}>
                  {b.mode === "early"
                    ? `Resilience ${b.resilience} · Population ${b.population}${b.debt ? ` · Debt ${b.debt} (not subtracted)` : ""}`
                    : `Population ${b.population} + Resilience ${b.resilience}${b.debt ? ` − Debt ${b.debt}` : ""}`} · Repairs paid {b.flood_paid ?? 0}
                </span>
              </span>
              <span data-testid={`final-score-total-${b.color}`} className="shrink-0 tabular-nums" style={{ fontSize: 20, fontWeight: 700, color: "var(--ink)" }}>{b.total}</span>
            </li>
          ))}
        </ol>
      </section>
      <section data-testid="final-log-panel" data-capture="skip" className="flex-1 min-h-0 flex flex-col" style={{ background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius)", padding: "12px 14px" }}>
        <div className="shrink-0 flex items-center">
          <span className="section-label">Game log</span>
          <span className="ml-auto" style={{ fontSize: 12, color: "var(--ink-3)" }}>{log.length} entries</span>
        </div>
        <div data-testid="final-log-list" className="mt-2 flex-1 min-h-0 overflow-y-auto overscroll-contain pr-1" style={{ fontSize: 12, lineHeight: "16px" }}>
          {groups.map((g, gi) => (
            <div key={gi} className="mb-2">
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--ink-4)" }}>
                {g.round === 0 ? "Phase 1" : `Round ${g.round}`}
              </div>
              <ul className="mt-0.5 flex flex-col" style={{ gap: 2 }}>
                {g.entries.map((e, i) => (
                  <li key={i} style={{ color: "var(--ink-2)" }}>{e.text}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
